import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Rating,
  CircularProgress,
  Grid,
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { ratingsApi } from '../api/ratings';
import { TopRatedUser } from '../types';

export default function TopRatedPage() {
  const { data: topRated, isLoading, error } = useQuery({
    queryKey: ['topRated'],
    queryFn: () => ratingsApi.getTopRated(20),
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography color="error">Failed to load top rated users. Please try again later.</Typography>
      </Box>
    );
  }

  const getMedalColor = (index: number) => {
    switch (index) {
      case 0:
        return '#FFD700'; // Gold
      case 1:
        return '#C0C0C0'; // Silver
      case 2:
        return '#CD7F32'; // Bronze
      default:
        return undefined;
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <TrophyIcon sx={{ fontSize: 40, color: 'secondary.main' }} />
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Top Rated Users
        </Typography>
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        The most trusted dynasty fantasy football managers based on community ratings
      </Typography>

      {topRated && topRated.length > 0 ? (
        <Grid container spacing={2}>
          {topRated.map((user: TopRatedUser, index: number) => (
            <Grid item xs={12} key={user.sleeper_account_id}>
              <Card
                component={RouterLink}
                to={`/sleeper/${user.account?.sleeper_user_id}`}
                className="card-hover"
                sx={{
                  display: 'block',
                  textDecoration: 'none',
                  position: 'relative',
                  overflow: 'visible',
                }}
              >
                <CardContent>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item>
                      <Box
                        sx={{
                          position: 'relative',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {index < 3 && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: -8,
                              left: -8,
                              bgcolor: getMedalColor(index),
                              borderRadius: '50%',
                              width: 28,
                              height: 28,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: index === 0 ? '#000' : '#fff',
                              fontWeight: 700,
                              fontSize: '0.8rem',
                              boxShadow: 2,
                              zIndex: 1,
                            }}
                          >
                            {index + 1}
                          </Box>
                        )}
                        <Avatar
                          src={
                            user.account?.current_avatar_id
                              ? `https://sleepercdn.com/avatars/thumbs/${user.account.current_avatar_id}`
                              : undefined
                          }
                          sx={{
                            width: 64,
                            height: 64,
                            border: index < 3 ? `3px solid ${getMedalColor(index)}` : 'none',
                          }}
                        >
                          {user.account?.current_username?.[0]?.toUpperCase()}
                        </Avatar>
                      </Box>
                    </Grid>
                    <Grid item xs>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {index >= 3 && (
                          <Typography
                            variant="h6"
                            sx={{ color: 'text.secondary', fontWeight: 400, mr: 1 }}
                          >
                            #{index + 1}
                          </Typography>
                        )}
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {user.account?.current_username || 'Unknown'}
                        </Typography>
                        {user.account?.is_verified && (
                          <VerifiedIcon sx={{ fontSize: 18, color: 'success.main' }} />
                        )}
                      </Box>
                      {user.account?.current_display_name && (
                        <Typography variant="body2" color="text.secondary">
                          {user.account.current_display_name}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item>
                      <Box sx={{ textAlign: 'right' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
                          <Rating
                            value={user.overall_average}
                            precision={0.1}
                            readOnly
                            size="small"
                          />
                          <Typography
                            variant="h5"
                            sx={{ fontWeight: 700, color: 'secondary.main' }}
                          >
                            {Number(user.overall_average).toFixed(1)}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {user.total_ratings} rating{user.total_ratings !== 1 ? 's' : ''}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <TrophyIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
          <Typography color="text.secondary">
            No rated users yet. Be the first to rate someone!
          </Typography>
        </Box>
      )}

      <Box sx={{ mt: 4, p: 3, bgcolor: 'background.paper', borderRadius: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Users need at least 3 ratings to appear on this leaderboard.
          <br />
          Ratings are calculated as the average across all 5 categories.
        </Typography>
      </Box>
    </Box>
  );
}

